import React from 'react';
import { faFlaskVial } from '@fortawesome/free-solid-svg-icons';
import BlogPost from '../BlogPost';
// Create: src/assets/images/blogs/ai-driven-semantics-results.jpg
import aiDrivenSemanticsImg from '../../../assets/images/blogs/Ontology-Tax-real-world-results.png';

const AiDrivenSemanticsResults = () => (
    <BlogPost
        title="AI Driven Semantics: Real World Results"
        subtitle="Real semantic results from Harrison's Principles of Internal Medicine. All without hallucination and no hand-built ontology."
        category="Cognition"
        categoryIcon={faFlaskVial}
        color="#003366"
        date="March 2026"
        readTime="4 min read"
        image={aiDrivenSemanticsImg}
        imageAlt="AI-driven semantics - real world results from clinical text"
        ctaHeading="Want to see these results for your domain?"
        ctaText="Book a discovery call and we'll show you how AI-native semantics delivers real results in minutes, not months."
        seoDescription="Real results from AI-native semantics: 122,312 sentences encoded, 7,916 clinical concepts identified from Harrison's in 30 minutes."
        seoKeywords="AI semantics, clinical AI, knowledge extraction, Harrison's Internal Medicine, Nebulyx Cognition, ConsistentAI"
        slug="ai-driven-semantics-results"
    >
        <p>
            Following my post about the Ontology Tax, I had some push-back (some constructive
            - love these, but some just wanted to rant - which I don't have time for).
        </p>

        <p>
            A lot of the genuine challenges were around whether the claims hold up in practice.
        </p>

        <p>
            <strong>So I did a use-case this weekend.</strong>
        </p>

        <h3>The Test</h3>

        <p>
            I pointed the system at Harrison's Principles of Internal Medicine. 5,113 pages.
            One of the most comprehensive clinical textbooks ever published.
        </p>

        <p>
            Not a curated dataset - a real medical reference that clinicians train on.
        </p>

        <h3>The Results</h3>

        <p>
            → 16.9 million characters extracted with page-level tracking<br />
            → 122,312 sentences encoded and structurally understood<br />
            → 7,916 clinical concepts identified<br />
            → 22,224 domain terms discovered automatically
        </p>

        <p>
            <strong>Processing: 20-30 minutes, end-to-end.</strong>
        </p>

        <p>
            Then I asked it: <em>"How should a clinician manage a patient presenting with
            chest pain?"</em>
        </p>

        <blockquote className="blog-post-quote">
            "In patients with acute chest discomfort, the clinician must first assess the
            patient's respiratory and hemodynamic status"
        </blockquote>

        <p>
            Plus guidance on differentiating life-threatening conditions from chronic
            presentations - all cited and all traceable to specific sections.
        </p>

        <p>
            <strong>Real semantic results, all without hallucination and no hand-built
            ontology.</strong>
        </p>

        <h3>What People Misunderstood</h3>

        <p>
            We're not building ontologies faster, or at all. <strong>We're building AI-native
            semantics.</strong>
        </p>

        <p>
            The system discovers semantic structure directly from the language itself. It
            understands that a sentence carries an obligation, a recommendation, or a
            prohibition. Not because someone mapped those concepts into a taxonomy, but
            because the language tells you.
        </p>

        <p>
            "Shall report all transactions" and "clinicians should offer CBT" share the same
            linguistic shape. Someone must do something, with a degree of authority. The
            system recognises that natively and indexes it with the source, page, section
            and context.
        </p>

        <p>
            SMEs then go in and adjust. We are building business-friendly UIs for this now.
        </p>

        <h3>The Bottom Line</h3>

        <p>
            Feed it documents, SME tweaks, then ask questions - that give accurate results
            with citations and traceable answers. All in hours, not months.
        </p>

        <p>
            That's not a cheaper path to an ontology. It's a different path entirely.
        </p>

        <p>
            And when source material changes? Process it again - the knowledge stays current
            without a maintenance team.
        </p>

        <p>
            Is it perfect (i.e. zero-shot)? No. The system gets sharper with more training
            and SME input.
        </p>

        <p>
            But organisations can now use AI to get rich semantic knowledge bases in days,
            not months. 🎯
        </p>
    </BlogPost>
);

export default AiDrivenSemanticsResults;
